// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { FlaskConical, Play } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOctoEvaluateQuery } from "@/api/hooks/use-octo-evaluate";
import type { OctoRuleStage } from "@/api/hooks/use-octo-rules";
import type { CodeLanguage } from "@/components/shared/code-highlight";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { OctoFlowDiagram } from "@/features/octo/octo-flow-diagram";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useDebugMode } from "@/lib/debug-mode";
import { cn } from "@/lib/utils";

const STAGES: OctoRuleStage[] = ["creation", "visibility", "workability"];

type ProductMode = "upc" | "itemNumber";

/** The inputs that constitute an evaluation, applied together when Evaluate is pressed. */
interface EvaluationContext {
  taskType: string;
  upc: string;
  itemNumber: string;
  evaluatedAt: string;
}

const EMPTY_CONTEXT: EvaluationContext = { taskType: "", upc: "", itemNumber: "", evaluatedAt: "" };

interface EvaluateOctoRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dry-run the octo rules for a store/stage and render the decision as a top-to-bottom flow.
 *
 * Two phases, matching how the engine itself works. Picking a store and stage resolves the rule
 * chain — scope-resolved and ordered by priority, exactly as production would walk it — with no
 * verdicts on it. Supplying an evaluation context (task type, optionally a product and a
 * "current" time) and pressing Evaluate replays the decision over that chain, marking what
 * matched, what was skipped, and where the engine stopped.
 */
export function EvaluateOctoRulesDialog({ open, onOpenChange }: EvaluateOctoRulesDialogProps) {
  const { t } = useTranslation("back-office");
  const { debugMode } = useDebugMode();
  const { data: context } = useBackofficeContext();

  const [storeName, setStoreName] = useState("");
  const [stage, setStage] = useState<OctoRuleStage>("creation");

  // Staged inputs — edited freely, only sent when Evaluate is pressed, so typing a UPC doesn't
  // fire a request per keystroke.
  const [draft, setDraft] = useState<EvaluationContext>(EMPTY_CONTEXT);
  const [productMode, setProductMode] = useState<ProductMode>("upc");
  const [applied, setApplied] = useState<EvaluationContext>(EMPTY_CONTEXT);

  useEffect(
    function resetStateOnOpen() {
      if (open) {
        setStoreName("");
        setStage("creation");
        setDraft(EMPTY_CONTEXT);
        setProductMode("upc");
        setApplied(EMPTY_CONTEXT);
      }
    },
    [open],
  );

  const storeOptions = useMemo(
    () => (context?.stores ?? []).map((s) => ({ label: s.display, value: s.value })),
    [context?.stores],
  );
  const taskTypeOptions = useMemo(
    () => (context?.taskTypes ?? []).map((type) => ({ label: type, value: type })),
    [context?.taskTypes],
  );

  const { data, isFetching, isError, error } = useOctoEvaluateQuery(
    {
      storeName,
      stage,
      taskType: applied.taskType || undefined,
      upc: applied.upc || undefined,
      itemNumber: applied.itemNumber || undefined,
      evaluatedAt: applied.evaluatedAt || undefined,
    },
    { enabled: open && !!storeName },
  );

  function handleTargetChange(next: { storeName?: string; stage?: OctoRuleStage }) {
    if (next.storeName !== undefined) setStoreName(next.storeName);
    if (next.stage !== undefined) setStage(next.stage);
    setApplied(EMPTY_CONTEXT);
  }

  function evaluate() {
    setApplied({
      taskType: draft.taskType,
      // Only the selected identifier is sent; the other is cleared so a stale value from the
      // other mode can never resolve a different product than the one on screen.
      upc: productMode === "upc" ? draft.upc.trim() : "",
      itemNumber: productMode === "itemNumber" ? draft.itemNumber.trim() : "",
      evaluatedAt: draft.evaluatedAt,
    });
  }

  const canEvaluate = !!storeName && !!draft.taskType;
  const productValue = productMode === "upc" ? draft.upc : draft.itemNumber;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 overflow-hidden flex flex-col" aria-describedby={undefined}>
        <div className="flex flex-row max-h-[calc(85vh-4rem)]">
          <WizardPane icon={FlaskConical} />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-6 pt-6">
            <DialogHeader>
              <DialogTitle>{t("octoRules.evaluate.title")}</DialogTitle>
            </DialogHeader>

            <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
              <div className="flex flex-col gap-4">
                {/* ── Target: which rule chain to walk ── */}
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex min-w-48 flex-1 flex-col gap-2">
                    <Label>{t("octoRules.form.store")}</Label>
                    <VirtualSelectPopover
                      value={storeName}
                      onChange={(v) => handleTargetChange({ storeName: v })}
                      options={storeOptions}
                      placeholder={t("octoRules.form.selectStore")}
                      searchPlaceholder={t("users.list.filter.searchStores")}
                      emptyText={t("users.list.filter.noStores")}
                    />
                  </div>
                  <div className="flex w-44 flex-col gap-2">
                    <Label>{t("octoRules.fields.stage")}</Label>
                    <Select value={stage} onValueChange={(v) => handleTargetChange({ stage: v as OctoRuleStage })}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {t(`octoRules.stage.${s}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* ── Evaluation context: what the chain is walked against ── */}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{t("octoRules.evaluate.contextTitle")}</span>
                    <span className="text-xs text-muted-foreground">{t("octoRules.evaluate.contextHint")}</span>
                  </div>

                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex min-w-44 flex-1 flex-col gap-2">
                      <Label>
                        {t("octoRules.evaluate.taskType")}
                        <span className="ml-0.5 text-destructive">*</span>
                      </Label>
                      <VirtualSelectPopover
                        value={draft.taskType}
                        onChange={(v) => setDraft((prev) => ({ ...prev, taskType: v }))}
                        options={taskTypeOptions}
                        placeholder={t("octoRules.evaluate.selectTaskType")}
                        searchPlaceholder={t("octoRules.evaluate.searchTaskTypes")}
                        emptyText={t("octoRules.evaluate.noTaskTypes")}
                        clearable
                      />
                    </div>
                    <div className="flex w-56 flex-col gap-2">
                      <Label htmlFor="evaluate-datetime">{t("octoRules.evaluate.evaluatedAt")}</Label>
                      <Input
                        id="evaluate-datetime"
                        type="datetime-local"
                        value={draft.evaluatedAt}
                        onChange={(e) => setDraft((prev) => ({ ...prev, evaluatedAt: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Product identity — one of UPC / item number, resolved against the store. The
                      mode toggle sits inside the field so the two read as a single control; the
                      wrapper mirrors Input's border/focus treatment and takes the focus ring, since
                      the inner input itself is borderless. */}
                  <div className="border-input flex h-9 w-full items-center rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30">
                    <div className="flex shrink-0 items-center gap-0.5 pl-1">
                      {(["upc", "itemNumber"] as ProductMode[]).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setProductMode(mode)}
                          className={cn(
                            "rounded px-2 py-1 text-xs transition-colors",
                            productMode === mode
                              ? "bg-accent font-medium text-accent-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {t(`octoRules.evaluate.product.${mode}`)}
                        </button>
                      ))}
                    </div>
                    <div className="mx-2 h-5 w-px shrink-0 bg-border" />
                    <input
                      value={productValue}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          [productMode === "upc" ? "upc" : "itemNumber"]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && canEvaluate) evaluate();
                      }}
                      placeholder={t(`octoRules.evaluate.product.${productMode}Placeholder`)}
                      // Regular sans face, but tabular figures so the digits of a UPC stay evenly
                      // spaced and a mistyped one is easy to spot — the numeric half of RowId's
                      // treatment, without the monospaced letters.
                      className="placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground h-full min-w-0 flex-1 bg-transparent pr-3 text-base tabular-nums outline-none md:text-sm"
                      autoComplete="off"
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {data?.store.timeZone
                      ? t("octoRules.evaluate.timezoneHint", { timezone: data.store.timeZone })
                      : t("octoRules.evaluate.timezoneHintGeneric")}
                  </p>
                </div>

                <Separator />

                {/* ── The flow ── */}
                {!storeName && (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t("octoRules.evaluate.pickStore")}
                  </div>
                )}
                {/* Keep the previous flow on screen while re-running so the dialog doesn't blank
                    out between evaluations — the spinner alone signals the refresh. */}
                {storeName && isFetching && <InlineSpinner label={t("octoRules.evaluate.running")} />}
                {storeName && !isFetching && isError && (
                  <div className="rounded-md border border-destructive/60 bg-destructive/5 p-4 text-sm text-destructive">
                    {error instanceof Error ? error.message : t("octoRules.evaluate.failed")}
                  </div>
                )}
                {storeName && !isError && data && (
                  <>
                    <div className={cn(isFetching && "opacity-50")}>
                      <OctoFlowDiagram result={data} />
                    </div>
                    {debugMode && (
                      <div className="flex flex-col gap-2">
                        <DebugBlock
                          title={t("octoRules.evaluate.debug.compiledRule")}
                          value={data.compiledRule}
                          lang="cel"
                        />
                        <DebugBlock
                          title={t("octoRules.evaluate.debug.boundContext")}
                          value={Object.keys(data.context).length > 0 ? JSON.stringify(data.context) : ""}
                          lang="json"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("buttons.close")}
          </Button>
          <Button onClick={evaluate} disabled={!canEvaluate || isFetching}>
            <Play className="h-4 w-4" />
            {t("octoRules.evaluate.runButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Collapsible raw-value panel, shown only in debug mode. */
function DebugBlock({ title, value, lang }: { title: string; value: string; lang: CodeLanguage }) {
  const { t } = useTranslation("back-office");
  return (
    <Collapsible>
      <CollapsibleTrigger className="w-full rounded-md border px-3 py-2 text-left text-xs font-medium text-muted-foreground hover:bg-accent">
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {value ? (
          <CodeHighlight
            value={value}
            lang={lang}
            className="mt-1 max-h-64 [&_pre]:min-w-0 [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
          />
        ) : (
          <p className="mt-1 rounded-md bg-muted p-3 text-xs text-muted-foreground">
            {t("octoRules.evaluate.debug.empty")}
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
